import { useState } from 'react'
import { FetchTranscriptResponse, Transcript } from '../swagger'
import { extractedErrorObject, transcriptsApi } from '../api/swagger'
import { useTranslation } from 'react-i18next'
import { useMountEffect } from './useMountEffect'
import { enqueueSnackbar } from 'notistack'
import { useNotistackSnackbarKeyContext } from '../components/Context/NotistackSnackbarKeyProvider'
import useLoadingContext from './useLoadingContext'
import { useLoadingIds } from './useLoadingIds'
import { useAppSnackbar } from './useAppSnackbar'

export interface FetchTranscriptInBatchHookProps {
  transcriptKeys: number[]
}

export const useFetchTranscriptInBatch = ({
  transcriptKeys,
}: FetchTranscriptInBatchHookProps): {
  transcripts?: Transcript[]
  loadingId: string
  isLoading: boolean
} => {
  const [transcripts, setTranscripts] = useState<Transcript[]>()
  const { handleError } = useAppSnackbar()
  const {
    UseFetchTranscriptInBatch: { fetchTranscript: fetchTranscriptLoadingId },
  } = useLoadingIds()
  const { addSnackbarKey } = useNotistackSnackbarKeyContext()
  const { t } = useTranslation()

  const errorMessage = t(
    'Hooks.FetchTranscriptInBatch.Error.Default',
    'An error occurred while attempting to load transcripts'
  )

  const fetchTranscriptsInBatch = async () => {
    try {
      const results = await Promise.allSettled(
        transcriptKeys.map((transcriptKey) =>
          transcriptsApi.fetchTranscript({
            transcriptKey,
          })
        )
      )

      // Separate rejections from success messages
      const rejections = results.filter((it) => it.status === 'rejected')
      const errorMessage: Set<string> = new Set()

      for (const rejection of rejections) {
        errorMessage.add(
          `${
            (
              await extractedErrorObject(
                (rejection as PromiseRejectedResult).reason
              )
            )?.message
          } \n`
        )
      }

      for (const msg of errorMessage) {
        addSnackbarKey(enqueueSnackbar(msg, { variant: 'error' }))
      }

      const successes = results.filter((it) => it.status === 'fulfilled')
      const resultArray: Transcript[] = []

      for (const success of successes) {
        resultArray.push(
          (success as PromiseFulfilledResult<FetchTranscriptResponse>).value
            .transcript
        )
      }

      setTranscripts(resultArray)
    } catch (error) {
      await handleError(error, errorMessage)
    }
  }

  const { triggerFetch, isLoading } = useLoadingContext({
    asyncFunction: fetchTranscriptsInBatch,
    loadingId: fetchTranscriptLoadingId,
  })

  useMountEffect(() => {
    triggerFetch()
  })

  return {
    transcripts,
    loadingId: fetchTranscriptLoadingId,
    isLoading,
  }
}
